import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { flatten, flow, isEmpty, max } from "lodash";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../../settings/subtitleDescription";
import {
  getLanguageOptionFromBrowser,
  LanguageOption,
  LanguagePicker
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { useStore } from "../../../models";
import { FormPlaceholder } from "../../settings/settingsForms/fullWidthPlaceholder/formPlaceholder";
import styles from "./modelConfiguration.css";
import intentConfigStyles from "./intentConfiguration.css";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { VerticalSeparator } from "../../settings/settingsForms/separators/verticalSeparator/verticalSeparator";
import { IntentConfiguration } from "./intentConfiguration";
import classNames from "classnames";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { ChatMessageInput } from "../../../containers/chat/chatMessageInput";
import {
  IntentForm,
  IntentDto,
  TrainingInfo,
  IntentWithDatasetInfo
} from "@closerplatform/spinner-openapi";
import { getNumericFormatTimestamp } from "@fidget/common/utils/timeFormatter";
import { logger } from "@fidget/common/utils";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { SimpleIndicator } from "@fidget/common/components/indicators/simpleIndicator/simpleIndicator";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { DotIndicator } from "@fidget/common/components/indicators/dotIndicator/dotIndicator";
import { replaceText, templatePairs } from "../../../../src/models/intent/intentStoreUtils";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../../models/agentServices";
import { ElementForm } from "../../settings/settingsForms/elementForm/elementForm/elementForm";
import { ElementFormActions } from "../../settings/settingsForms/elementForm/actions/elementFormActions";
import { ElementFormEditIcon } from "../../settings/settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormDeleteIcon } from "../../settings/settingsForms/elementForm/icons/elementFormDeleteIcon";
import { CheckBoxInputFieldState } from "@fidget/common/components/form/checkboxInput";
import { SettingsForm } from "../../settings/settingsForm";
import { DeleteConfirmation } from "../../settings/settingsForms/deleteConfirmation/deleteConfirmation";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";
import {
  IntentDtoWithDatasetInfo,
  IntentWithDatasetInfoFormType,
  NewIntentFormType
} from "./types";
import {
  copyExistingIntentForm,
  createNewIntentForm,
  mapDtoToDtoWithDatasetInfo,
  mapDtoToIntentWithDatasetInfo,
  mapDtoWithDatasetInfoToForm,
  mapFormToIntent,
  mapIntentWithDatasetInfoFormToUpdateForm,
  mapIntentWithDatasetInfoFormTypeToIntentDto,
  mapIntentWithDatasetInfoToDtoWithDatasetInfo
} from "./utils";
import {
  ListHook,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../../settings/models/useSettingsList/types";
import {
  useSettingsList,
  withSelectables,
  withFilters
} from "../../settings/models/useSettingsList/useSettingsList";
import { usePreloader, withPreloading } from "../../HOC/withPreloading/withPreloading";
import { EditInputFieldState } from "@fidget/common/components/form/editInput";
import { ElementFormSaveDiscardIcon } from "../../settings/settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { FilterWrapperProvider } from "../../settings/settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { ListFilterWrapper } from "../../settings/settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { BooleanListFilter } from "../../settings/settingsForms/filters/booleanListFilter";

interface Props {
  resetScrollOffset: () => void;
}

export const AiModelConfigurationComponent = observer((props: Props) => {
  const {
    newItem,
    items,
    editedItems,
    filteredItems,
    updatedIntentsNumber,
    modelInfo,
    messageMaxAllowedCharacters,

    currentlyEditedIntent,
    setCurrentlyEditedIntent,

    currentLanguage,
    setCurrentLanguage,

    saveNewItem,
    discardNewItem,
    apiDeleteItem,
    updateItem,

    onSubmitAll,
    onDiscardAll,
    uiRemoveExistingItem,
    handleDeleteError,

    handleCreateNewClick,
    handleIntentEditClick,
    handleTrainModelClick,
    isIntentInCurrentLanguage
  } = useAiModelConfigurationState(props);

  const renderTitle = () => (
    <div className={commonStyles.title}>
      <L10n id={"ai.navigation.modelConfiguration"} />
    </div>
  );

  const renderLanguagePicker = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.languagePicker.title")}
        description={intl.formatMessage("ai.modelConfiguration.language.description")}
      />
      <div className={commonStyles.content}>
        <LanguagePicker
          onChange={(val) => (val ? setCurrentLanguage(val) : undefined)}
          currentLanguage={currentLanguage}
        />
      </div>
    </div>
  );

  const renderIntentsSection = () => (
    <div className={commonStyles.section}>
      {renderIntentsDescription()}
      {newItem ? renderAddIntentForm(newItem) : renderAddIntentPlaceholder()}
      {renderFormSettings()}
      {renderIntentsList()}
    </div>
  );

  const renderIntentsDescription = () => (
    <SubtitleDescription
      subtitle={intl.formatMessage("ai.modelConfiguration.intents.title")}
      description={intl.formatMessage("ai.modelConfiguration.intents.description")}
    />
  );

  const renderAddIntentPlaceholder = () => (
    <FormPlaceholder
      onClick={handleCreateNewClick}
      text={intl.formatMessage("ai.modelConfiguration.intents.createNew")}
    />
  );

  const renderAddIntentForm = (form: NewIntentFormType) => (
    <ElementForm isEdited className={styles["intent-form"]}>
      <div className={styles["intent-name"]}>
        <EditInputFieldState
          field={form.$.name}
          placeholder={intl.formatMessage("ai.modelConfiguration.intent.form.name.placeholder")}
          maxLength={staticConfig.aiIntentNameMaxCharacters}
          className={styles["intent-name"]}
        />
        <p className={classNames(styles["description-font"], styles["name-description"])}>
          {intl.formatMessage("ai.modelConfiguration.intent.form.name.description")}
        </p>
      </div>
      <VerticalSeparator />

      <div className={intentConfigStyles.input}>
        <ChatMessageInput
          inputChatState={form.$.response.$}
          onSendMessage={saveNewItem}
          placeholder={intl.formatMessage("ai.modelConfiguration.intent.form.response.placeholder")}
          activationSequence={staticConfig.mentionActivationSequence}
          delimeterSequence={staticConfig.mentionDelimeterSequence}
          hideFileUpload
        />
      </div>
      <p className={classNames(styles["description-font"], styles["response-description"])}>
        {intl.formatMessage("ai.modelConfiguration.intent.form.response.description", {
          charactersLimit: messageMaxAllowedCharacters
        })}
      </p>
      <div style={{ marginLeft: "auto" }}>
        <ElementFormActions>
          <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
        </ElementFormActions>
      </div>
    </ElementForm>
  );

  const renderSettingsForm = () => (
    <div>
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      />
    </div>
  );

  const renderSearch = () => (
    <div className={styles["intent-name"]}>
      <FilterWrapperProvider<IntentWithDatasetInfoFormType>>
        <ListFilterWrapper<IntentWithDatasetInfoFormType>
          placeholder={intl.formatMessage("ai.modelConfiguration.intent.filter.placeholder")}
          allCount={items.length}
          filteredCount={filteredItems.length}
          inputPredicateId="IntentNameFilter"
          inputPredicate={(item, input) => caseInsensitiveIncludes(item.$.intent.$, input)}
          headerText={intl.formatMessage("ai.modelConfiguration.intent.filter.header")}
          showCounter={false}
        >
          <BooleanListFilter<IntentWithDatasetInfoFormType>
            description={intl.formatMessage("ai.modelConfiguration.intent.form.active")}
            filterId="IntentActive"
            predicate={(item) => item.$.active.$}
          />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    </div>
  );

  const renderFormSettings = () =>
    items.length > 0 && (
      <div className={classNames(styles["filter-section"], commonStyles.content)}>
        {renderSearch()}
        {renderSettingsForm()}
      </div>
    );

  const renderModelTrainedAt = (model: TrainingInfo) =>
    model.lastTrainedAt && (
      <div>
        <p className={classNames(styles["description-font"], styles["stat-header"])}>
          {intl.formatMessage("ai.modelConfiguration.stats.lastTraining")}
        </p>
        <p className={styles["stat-content"]}>
          {getNumericFormatTimestamp(model.lastTrainedAt, intl.formatMessage("time.at"))}
        </p>
      </div>
    );

  const renderModelStatus = () => (
    <>
      <div>
        <p className={classNames(styles["description-font"], styles["stat-header"])}>
          {intl.formatMessage("ai.modelConfiguration.stats.status")}
        </p>
        <p className={styles["stat-content"]}>{renderStatusText(modelInfo?.status)}</p>
      </div>
      {!!updatedIntentsNumber && (
        <div className={styles["stat-indicator"]}>
          <TooltipComponent
            text={intl.formatMessage("ai.modelConfiguration.model.indicatorTooltip")}
            positionPreset="top"
            minWidth="100px"
          >
            <SimpleIndicator count={updatedIntentsNumber} />
          </TooltipComponent>
        </div>
      )}
      <VerticalSeparator />
    </>
  );

  const renderModelInfo = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("ai.modelConfiguration.trainModel.train")}
        description={intl.formatMessage("ai.modelConfiguration.trainModel.description")}
      />
      <div className={styles.stats}>
        <div className={styles["train-button"]}>
          <AgentButton
            onClick={handleTrainModelClick}
            disabled={!!modelInfo && modelInfo.status === "TrainingInProgress"}
          >
            {intl.formatMessage("ai.modelConfiguration.trainModel.train")}
          </AgentButton>
        </div>
        <VerticalSeparator />
        {renderModelStatus()}
        {modelInfo && renderModelTrainedAt(modelInfo)}
      </div>
    </div>
  );

  const renderStatusText = (status?: TrainingInfo["status"]) => {
    switch (status) {
      case "TrainingDone":
        return updatedIntentsNumber
          ? intl.formatMessage("ai.modelConfiguration.stats.status.toTrain")
          : intl.formatMessage("ai.modelConfiguration.stats.status.trained");
      case "TrainingInProgress":
        return intl.formatMessage("ai.modelConfiguration.stats.status.inProgress");
      case "TrainingNeverDone":
        return intl.formatMessage("ai.modelConfiguration.stats.status.notTrained");
      default:
        return intl.formatMessage("ai.modelConfiguration.stats.status.notTrained");
    }
  };

  const renderIntentsList = () => {
    return (
      <div>
        {filteredItems.filter(isIntentInCurrentLanguage).map((item) => (
          <DeleteConfirmation
            key={item.$.id.$}
            areYouSureText={intl.formatMessage("ai.modelConfiguration.intent.areYouSure")}
            succesfullyDeletedText={intl.formatMessage("ai.modelConfiguration.intent.removed")}
            onDelete={() => apiDeleteItem(item.$.id.$)}
            onError={handleDeleteError}
            onTimeoutPassed={() => uiRemoveExistingItem(item.$.id.$)}
            renderComponent={(onDeleteClick) => renderElementForm(item, onDeleteClick)}
          />
        ))}
      </div>
    );
  };

  const renderElementForm = (item: IntentWithDatasetInfoFormType, onDeleteClick: () => void) => {
    return (
      <ElementForm>
        <div className={classNames(styles["form-div"], styles["intent-name-section"])}>
          <p className={classNames(styles["description-font"], styles["stat-header"])}>
            {intl.formatMessage("ai.modelConfiguration.intent.form.name")}
          </p>
          <p className={styles["clickable-text"]} onClick={() => handleIntentEditClick(item)}>
            {item.$.intent.$}
          </p>
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-div"], styles["messages-number-section"])}>
          <p className={classNames(styles["description-font"], styles["stat-header"])}>
            {intl.formatMessage("ai.modelConfiguration.intent.form.number")}
          </p>
          <p className={styles["stat-content"]}>{item.$.datasetSize.$}</p>
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-div"], styles["date-training-section"])}>
          <p className={classNames(styles["description-font"], styles["stat-header"])}>
            {intl.formatMessage("ai.modelConfiguration.intent.form.added")}
          </p>
          <p className={styles["stat-content"]}>
            {getNumericFormatTimestamp(item.$.createdAt.$, intl.formatMessage("time.at"))}
          </p>
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-div"], styles["date-training-section"])}>
          <p className={classNames(styles["description-font"], styles["stat-header"])}>
            {intl.formatMessage("ai.modelConfiguration.intent.form.lastChange")}
          </p>
          <div className={classNames(styles["stat-content"], styles["last-changes"])}>
            {getNumericFormatTimestamp(
              max([item.$.datasetChangedAt.$, item.$.updatedAt.$]) || 0,
              intl.formatMessage("time.at")
            )}
            {modelInfo?.lastTrainedAt && item.$.updatedAt.$ > modelInfo.lastTrainedAt && (
              <div className={styles["dot-indicator"]}>
                <TooltipComponent
                  text={intl.formatMessage("ai.modelConfiguration.intent.form.untrainedTooltip")}
                  positionPreset="top"
                  minWidth="100px"
                >
                  <DotIndicator variant="alert" />
                </TooltipComponent>
              </div>
            )}
          </div>
        </div>
        <VerticalSeparator isHidden={item.$.datasetSize.$ !== 0} />
        <div className={classNames(styles["form-div"], styles["messages-tooltip-section"])}>
          <p className={classNames(styles["description-font"], styles["size-zero"])}>
            {item.$.datasetSize.$ === 0 &&
              intl.formatMessage("ai.modelConfiguration.intent.form.sizeZero")}
          </p>
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-div"], styles["checkbox-section"])}>
          <CheckBoxInputFieldState
            field={item.$.active}
            id={`active-${item.$.id.$}`}
            label={intl.formatMessage("ai.modelConfiguration.intent.form.active")}
            smallStyle
            displayNoColors
            noMarginBottom
          />
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-div"], styles["edit-delete-section"])}>
          <ElementFormActions>
            <ElementFormEditIcon onClick={() => handleIntentEditClick(item)} />
            <ElementFormDeleteIcon onClick={onDeleteClick} />
          </ElementFormActions>
        </div>
      </ElementForm>
    );
  };

  const render = () => (
    <>
      {renderTitle()}
      {renderLanguagePicker()}
      {renderModelInfo()}
      {filteredItems ? renderIntentsSection() : <CloserLoaderComponent />}
    </>
  );

  const renderIntentConfigurationScreen = (intent: IntentDto) => (
    <IntentConfiguration
      intentDto={intent}
      onGoBackClicked={() => setCurrentlyEditedIntent(undefined)}
      updateIntent={updateItem}
    />
  );

  return currentlyEditedIntent
    ? renderIntentConfigurationScreen(
        mapIntentWithDatasetInfoFormTypeToIntentDto(currentlyEditedIntent)
      )
    : render();
});

type HookParams = Params<
  IntentWithDatasetInfoFormType,
  NewIntentFormType,
  IntentDtoWithDatasetInfo
>;
type Hook = ListHook<IntentWithDatasetInfoFormType, NewIntentFormType>;
type HookSelectable = ListHookWithSelectables<
  IntentWithDatasetInfoFormType,
  NewIntentFormType,
  Hook
>;
type HookFilterable = ListHookWithFilters<
  IntentWithDatasetInfoFormType,
  NewIntentFormType,
  HookSelectable
>;

const useListHook = flow<[HookParams], Hook, HookSelectable, HookFilterable>(
  useSettingsList,
  withSelectables,
  withFilters
);

const useAiModelConfigurationState = (props: Props) => {
  const { currentUserStore, uiStore, intentStore } = useStore();
  const { setLoadingState } = usePreloader();
  const { spinnerService } = useAgentServices();

  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption>(
    getLanguageOptionFromBrowser()
  );
  const [modelInfo, setModelInfo] = useState<TrainingInfo>();
  const [currentlyEditedIntent, setCurrentlyEditedIntent] =
    useState<IntentWithDatasetInfoFormType>();

  const messageMaxAllowedCharacters =
    currentUserStore.orgConfig?.messageMaxAllowedCharactersForAgent ??
    staticConfig.maxMessageAllowedCharactersForAgent;

  const handleDeleteError = () => {
    uiStore.addErrorToast(intl.formatMessage("ai.modelConfiguration.intent.removed.error"));
  };

  const apiCreateItem = (form: NewIntentFormType): Promise<IntentDtoWithDatasetInfo> => {
    const createdIntent: IntentForm = {
      intent: form.$.name.$,
      locale: currentLanguage,
      response: replaceText(form.$.response.$.inputText, templatePairs, true),
      context: form.$.response.$.attachments.length > 0 ? form.$.response.$.attachments : undefined,
      active: false
    };

    return spinnerService.createIntent(createdIntent).then((dto) => {
      const intentWithDatasetInfo = mapDtoToIntentWithDatasetInfo(dto);
      intentStore.addIntentSuggestion(intentWithDatasetInfo);
      const intentDtoWithDatasetInfo = mapDtoToDtoWithDatasetInfo(dto);
      return intentDtoWithDatasetInfo;
    });
  };

  const loadAllIntents = (): Promise<IntentWithDatasetInfo[]>[] => {
    return Object.values(LanguageOption).map((language) => spinnerService.getIntents(language));
  };

  const apiGetItems = async () => {
    const allIntents = flatten(await Promise.all(loadAllIntents())).filter(
      (intent) => !intent.intentDto.deletedAt
    );

    intentStore.setIntentSuggestions(allIntents);
    return allIntents.map(mapIntentWithDatasetInfoToDtoWithDatasetInfo);
  };
  const apiDeleteItem = (id: string) => {
    return spinnerService.deleteIntent(id).then(() => {
      intentStore.deleteIntentSuggestion(id);
    });
  };

  const onSubmitCallback = (items: IntentWithDatasetInfoFormType[]) => {
    intentStore.setIntentSuggestions(items.map(mapFormToIntent));
    listHook.deselectAll();
  };

  const onDiscardCallback = () => {
    listHook.deselectAll();
  };

  const listHook = useListHook({
    apiGetItems,
    apiCreateItem,
    apiUpdateItem: (form) => {
      return spinnerService
        .updateIntent(form.$.id.$, mapIntentWithDatasetInfoFormToUpdateForm(form))
        .then((dto) => {
          const intentDtoWithDatasetInfo = mapDtoToDtoWithDatasetInfo(dto);
          return intentDtoWithDatasetInfo;
        });
    },
    apiDeleteItem,
    mapItemToForm: mapDtoWithDatasetInfoToForm,
    copyExistingForm: copyExistingIntentForm,
    createNewForm: createNewIntentForm,
    saveErrorId: "ai.modelConfiguration.intent.created.error",
    deleteErrorId: "ai.modelConfiguration.intent.removed.error",
    onSubmitCallback,
    onDiscardCallback,
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);
      item.$.active.onUpdate(setEdited);
      item.$.intentDescription.onUpdate(setEdited);
      item.$.intent.onUpdate(setEdited);
    });
  }, [listHook.items]);

  const handleIntentEditClick = (intent: IntentWithDatasetInfoFormType) => {
    if (!isEmpty(listHook.editedItems)) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }
    props.resetScrollOffset();
    setCurrentlyEditedIntent(intent);
  };

  const isEnoughIntentsToTrain = (): boolean =>
    listHook.items !== undefined &&
    listHook.items.filter((intent) => intent.$.datasetSize.$ > 0).length >=
      staticConfig.aiMinNonEmptyIntentsToTrain;

  const loadModel = async () => {
    try {
      const model = await spinnerService.getLastTrainingInfo(currentLanguage);
      setModelInfo(model);
    } catch (e) {
      if ((e as Response).status !== HttpStatusCode.NOT_FOUND) {
        logger.error({ message: "Error loading model", error: e });
        uiStore.addGenericErrorToast();
      }
      setModelInfo(undefined);
    }
  };

  useEffect(() => {
    loadModel();
  }, [currentLanguage]);

  const handleTrainModelClick = async () => {
    if (!isEnoughIntentsToTrain()) {
      uiStore.addErrorToast(
        intl.formatMessage("ai.modelConfiguration.trainModel.notEnoughIntentsError")
      );
      return;
    }

    const status = modelInfo?.status;

    try {
      if (modelInfo) {
        setModelInfo({
          ...modelInfo,
          status: "TrainingInProgress" as unknown as TrainingInfo["status"]
        });
      }

      await spinnerService.trainModel({ locale: currentLanguage });
    } catch (e) {
      logger.error({ message: "Train model failed", error: e });
      uiStore.addGenericErrorToast();

      if (modelInfo && status) {
        setModelInfo({ ...modelInfo, status });
      }
    }
  };

  const updateItem = (intentDto: IntentDto) => {
    const updatedForm = listHook.items.find((intentForm) => intentForm.$.id.$ === intentDto.id);
    if (!updatedForm) {
      return;
    }

    updatedForm.$.intentDescription.$ = intentDto.intentDescription;
    updatedForm.$.response.$ = intentDto.response;
    updatedForm.$.updatedAt.$ = Date.now();

    intentStore.updateIntentSuggestion(mapFormToIntent(updatedForm));
  };

  const isIntentInCurrentLanguage = (intent: IntentWithDatasetInfoFormType) =>
    caseInsensitiveIncludes(intent.$.locale.$, currentLanguage.split("-")[0]!);

  const lastTrainedAt = modelInfo && modelInfo.lastTrainedAt;
  const updatedIntentsNumber = lastTrainedAt
    ? listHook.items.filter(
        (intent) => intent.$.updatedAt.$ > lastTrainedAt && isIntentInCurrentLanguage(intent)
      ).length
    : 0;
  return {
    ...listHook,
    currentLanguage,
    modelInfo,
    currentlyEditedIntent,
    updatedIntentsNumber,
    messageMaxAllowedCharacters,
    setCurrentLanguage,
    handleIntentEditClick,
    handleTrainModelClick,
    setCurrentlyEditedIntent,
    handleDeleteError,
    updateItem,
    isIntentInCurrentLanguage
  };
};

export const AiModelConfiguration = withPreloading(AiModelConfigurationComponent);
